import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  createProjectVitalIssue,
  updateProjectVitalIssue,
} from '../services/projectVitalService';
import type {
  ProjectVitalIssue,
  ProjectVitalIssueStatus,
} from '../types/projectVital';

type Props = {
  open: boolean;
  onClose: () => void;
  issue?: ProjectVitalIssue | null;
};

const AREAS = [
  'Personal',
  'Profesional',
  'Finanzas',
  'Administración',
  'Salud y deporte',
  'Alimentación',
  'Formación',
  'Viajes y ocio',
  'Proyecto Vital',
  'Sin clasificar',
];

const STATUSES: Array<{
  value: ProjectVitalIssueStatus;
  label: string;
}> = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'en_curso', label: 'En curso' },
  { value: 'esperando', label: 'Esperando' },
  { value: 'bloqueado', label: 'Bloqueado' },
  { value: 'resuelto', label: 'Resuelto' },
];

export default function ProjectVitalIssueDialog({
  open,
  onClose,
  issue = null,
}: Props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [title, setTitle] = useState('');
  const [area, setArea] = useState('Sin clasificar');
  const [status, setStatus] =
    useState<ProjectVitalIssueStatus>('pendiente');
  const [nextStep, setNextStep] = useState('');
  const [targetDate, setTargetDate] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }

    if (issue) {
      setTitle(issue.title);
      setArea(issue.area);
      setStatus(issue.status);
      setNextStep(issue.next_step || '');
      setTargetDate(issue.target_date || '');
      return;
    }

    setTitle('');
    setArea('Sin clasificar');
    setStatus('pendiente');
    setNextStep('');
    setTargetDate('');
  }, [open, issue]);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title: title.trim(),
        area,
        status,
        next_step: nextStep.trim() || null,
        target_date: targetDate || null,
      };

      return issue
        ? updateProjectVitalIssue(issue.id, payload)
        : createProjectVitalIssue(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['project-vital', 'issues'],
      });

      enqueueSnackbar(
        issue ? 'Asunto actualizado.' : 'Asunto creado.',
        { variant: 'success' },
      );

      onClose();
    },
    onError: () => {
      enqueueSnackbar(
        issue
          ? 'No se pudo actualizar el asunto.'
          : 'No se pudo crear el asunto.',
        { variant: 'error' },
      );
    },
  });

  return (
    <Dialog
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: '#111c2f',
          border: fullScreen
            ? 'none'
            : '1px solid rgba(148,163,184,0.16)',
          borderRadius: fullScreen ? 0 : 3.5,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 850 }}>
        {issue ? 'Editar asunto' : 'Nuevo asunto'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.2} sx={{ pt: 1.5 }}>
          <TextField
            label="Título"
            fullWidth
            autoFocus
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={mutation.isPending}
          />

          <FormControl fullWidth>
            <InputLabel>Área</InputLabel>
            <Select
              label="Área"
              value={area}
              onChange={(event) => setArea(event.target.value)}
              disabled={mutation.isPending}
            >
              {AREAS.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Estado</InputLabel>
            <Select
              label="Estado"
              value={status}
              onChange={(event) =>
                setStatus(
                  event.target.value as ProjectVitalIssueStatus,
                )
              }
              disabled={mutation.isPending}
            >
              {STATUSES.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Próximo paso"
            placeholder="¿Qué tiene que ocurrir ahora?"
            fullWidth
            multiline
            minRows={2}
            value={nextStep}
            onChange={(event) => setNextStep(event.target.value)}
            disabled={mutation.isPending}
          />

          <TextField
            label="Fecha objetivo"
            type="date"
            fullWidth
            value={targetDate}
            onChange={(event) => setTargetDate(event.target.value)}
            disabled={mutation.isPending}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: fullScreen
            ? 'calc(20px + env(safe-area-inset-bottom))'
            : 2.5,
        }}
      >
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancelar
        </Button>

        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !title.trim()}
        >
          {mutation.isPending
            ? 'Guardando...'
            : issue
              ? 'Guardar cambios'
              : 'Crear asunto'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
